import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaTable, FaCheckCircle, FaInfoCircle, FaExclamationTriangle, FaSpinner, FaLightbulb } from 'react-icons/fa';
import { apiService } from '../../services/apiService';

const TableAcronymModal = ({ 
  isOpen, 
  onClose, 
  onConfirm,
  tableName,
  themeColor = 'emerald' 
}) => {
  const [acronym, setAcronym] = useState('');
  const [suggestedOptions, setSuggestedOptions] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [existingAcronyms, setExistingAcronyms] = useState(new Set());
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);

  // Algoritmo intelligente per generare acronimi sensati da un nome tabella
  const generateAcronyms = useCallback((name) => {
    if (!name) return [];
    
    const originalName = name.trim();
    const cleanName = originalName.replace(/[^a-zA-Z]/g, '');
    const upperName = cleanName.toUpperCase();
    
    const acronyms = [];
    
    // ALGORITMO 1: CamelCase/PascalCase Analysis (MASSIMA PRIORITÀ)
    // DocumentiArchiviati -> DA_
    const camelMatches = originalName.match(/[A-Z][a-z]*/g) || [];
    if (camelMatches.length >= 2) {
      // Prendi prime lettere di ogni parola CamelCase
      let camelAcronym = '';
      for (let i = 0; i < Math.min(camelMatches.length, 3); i++) {
        camelAcronym += camelMatches[i][0].toUpperCase();
      }
      
      if (camelAcronym.length === 2) {
        // DocumentiArchiviati -> DA + R (prima consonante significativa del secondo termine)
        const secondWord = camelMatches[1] || '';
        const consonants = secondWord.substring(1).match(/[BCDFGHJKLMNPQRSTVWXYZ]/i);
        if (consonants) {
          camelAcronym += consonants[0].toUpperCase();
        } else if (secondWord.length > 1) {
          camelAcronym += secondWord[1].toUpperCase();
        }
      }
      
      if (camelAcronym.length === 3) {
        acronyms.push(camelAcronym);
      }
    }
    
    // ALGORITMO 2: Prime sillabe significative
    // DocumentiArchiviati -> DOC + ARC + (I) -> DAR
    if (camelMatches.length >= 2) {
      const firstWord = camelMatches[0] || '';
      const secondWord = camelMatches[1] || '';
      
      // Prendi prime lettere + consonante caratteristica
      if (firstWord.length >= 2 && secondWord.length >= 2) {
        const first = firstWord[0].toUpperCase();
        const second = secondWord[0].toUpperCase();
        
        // Trova consonante finale caratteristica del secondo termine
        const endConsonants = secondWord.match(/[BCDFGHJKLMNPQRSTVWXYZ]/gi);
        const lastCons = endConsonants ? endConsonants[endConsonants.length - 1].toUpperCase() : secondWord[secondWord.length - 1].toUpperCase();
        
        acronyms.push(first + second + lastCons);
      }
    }
    
    // ALGORITMO 3: Pattern fonetico avanzato
    // Prendi consonanti più "parlanti" dal nome
    if (upperName.length >= 6) {
      const consonants = upperName.match(/[BCDFGHJKLMNPQRSTVWXYZ]/g) || [];
      
      if (consonants.length >= 3) {
        // Prima consonante + consonante a metà + consonante finale
        const first = consonants[0];
        const mid = consonants[Math.floor(consonants.length / 2)];
        const last = consonants[consonants.length - 1];
        
        if (first !== mid && mid !== last && first !== last) {
          acronyms.push(first + mid + last);
        }
      }
    }
    
    // ALGORITMO 4: Analisi sillabica intelligente
    // Divide in sillabe e prendi lettere significative
    const syllables = [];
    for (let i = 0; i < originalName.length; i++) {
      const char = originalName[i];
      if (/[A-Z]/.test(char) || i === 0) {
        syllables.push(i);
      }
    }
    
    if (syllables.length >= 2) {
      const parts = [];
      for (let i = 0; i < syllables.length; i++) {
        const start = syllables[i];
        const end = syllables[i + 1] || originalName.length;
        const part = originalName.substring(start, end);
        if (part.length > 0) parts.push(part);
      }
      
      if (parts.length >= 2) {
        // DocumentiArchiviati -> Documenti=DOC, Archiviati=ARC -> DCA, DAR, etc
        const p1 = parts[0].toUpperCase();
        const p2 = parts[1].toUpperCase();
        
        // Variante 1: D + A + R (prima + prima + consonante finale)
        if (p1.length >= 1 && p2.length >= 2) {
          const endCons = p2.match(/[BCDFGHJKLMNPQRSTVWXYZ]/g);
          if (endCons && endCons.length > 0) {
            acronyms.push(p1[0] + p2[0] + endCons[endCons.length - 1]);
          }
        }
        
        // Variante 2: DO + AR -> DAR
        if (p1.length >= 2 && p2.length >= 2) {
          acronyms.push(p1[0] + p1[1] + p2[0]);
        }
      }
    }
    
    // ALGORITMO 5: Pattern posizionale ottimizzato
    if (upperName.length >= 5) {
      const first = upperName[0];
      const mid = upperName[Math.floor(upperName.length * 0.4)]; // 40% invece che 50%
      const last = upperName[upperName.length - 1];
      
      if (first !== mid && mid !== last && first !== last) {
        acronyms.push(first + mid + last);
      }
    }
    
    // ALGORITMO 6: Consonanti distribuite
    const consonants = upperName.match(/[BCDFGHJKLMNPQRSTVWXYZ]/g) || [];
    if (consonants.length >= 4) {
      // Prendi 1a, consonante al 30%, consonante al 70%
      const first = consonants[0];
      const mid = consonants[Math.floor(consonants.length * 0.3)];
      const late = consonants[Math.floor(consonants.length * 0.7)];
      
      if (first !== mid && mid !== late && first !== late) {
        acronyms.push(first + mid + late);
      }
    }
    
    // ALGORITMO 7: Fallback con prime 3 lettere (solo se sensato)
    if (upperName.length >= 3) {
      const firstThree = upperName.substring(0, 3);
      acronyms.push(firstThree);
    }
    
    // ALGORITMO 8: Mix vocali/consonanti bilanciato
    const vowels = upperName.match(/[AEIOU]/g) || [];
    if (consonants.length >= 2 && vowels.length >= 1) {
      // 2 consonanti + 1 vocale in posizioni strategiche
      const c1 = consonants[0];
      const c2 = consonants[1];
      const v1 = vowels[0];
      
      acronyms.push(c1 + v1 + c2); // CVCpattern
      acronyms.push(c1 + c2 + v1); // CCV pattern
    }
    
    // ALGORITMO 9: Varianti creative aggiuntive per aumentare le opzioni
    if (acronyms.length < 10) { // Solo se abbiamo poche opzioni
      const baseChars = [originalName[0].toUpperCase()];
      
      // Aggiungi prime 2 consonanti più significative
      const allConsonants = upperName.match(/[BCDFGHJKLMNPQRSTVWXYZ]/g) || [];
      if (allConsonants.length >= 2) {
        const mostSignificant = allConsonants.filter((c, i, arr) => arr.indexOf(c) === i).slice(0, 3);
        
        // Combinazioni creative
        if (mostSignificant.length >= 3) {
          acronyms.push(mostSignificant[0] + mostSignificant[1] + mostSignificant[2]);
          acronyms.push(mostSignificant[0] + mostSignificant[2] + mostSignificant[1]);
          acronyms.push(mostSignificant[1] + mostSignificant[0] + mostSignificant[2]);
        }
      }
      
      // Pattern vocale-consonante
      const allVowels = upperName.match(/[AEIOU]/g) || [];
      if (allConsonants.length >= 2 && allVowels.length >= 1) {
        acronyms.push(allConsonants[0] + allVowels[0] + allConsonants[1]);
        if (allConsonants.length >= 3) {
          acronyms.push(allConsonants[0] + allVowels[0] + allConsonants[2]);
        }
      }
    }
    
    // Filtraggio e ranking finale
    const uniqueAcronyms = [...new Set(acronyms)]
      .filter(acr => acr && acr.length === 3)
      .map(acr => {
        // Sistema di punteggio migliorato
        const consonantCount = (acr.match(/[BCDFGHJKLMNPQRSTVWXYZ]/g) || []).length;
        const vowelCount = (acr.match(/[AEIOU]/g) || []).length;
        const uniqueChars = new Set(acr).size;
        const isPronounceable = consonantCount >= 1 && vowelCount >= 1;
        const hasGoodFlow = !/[BCDFGHJKLMNPQRSTVWXYZ]{3}/.test(acr); // Non 3 consonanti consecutive
        
        let score = 0;
        score += consonantCount * 3; // Consonanti pesano di più
        score += vowelCount * 2;
        score += uniqueChars * 2; // Caratteri unici bonus
        score += isPronounceable ? 5 : 0;
        score += hasGoodFlow ? 3 : 0;
        
        // Bonus per acronimi che iniziano con la prima lettera del nome
        if (acr[0] === originalName[0].toUpperCase()) {
          score += 4;
        }
        
        return { acronym: acr, score };
      })
      .sort((a, b) => b.score - a.score)
      .map(item => item.acronym)
      .slice(0, 12); // Genera più opzioni di base per avere più scelta dopo il filtro
    
    console.log(`[TableAcronymModal] Generated ${uniqueAcronyms.length} base acronyms for "${name}":`, uniqueAcronyms);
    return uniqueAcronyms;
  }, []);

  // Carica acronimi esistenti dal database
  const loadExistingAcronyms = useCallback(async () => {
    setIsLoadingExisting(true);
    try {
      const response = await apiService.get('/crud/SKE_Tabelle', {
        params: {
          limit: 1000, // Carica tutte le tabelle
          offset: 0,
          filters: JSON.stringify({}), // Nessun filtro per prendere tutto
        }
      });
      
      const tables = response.records || response.data?.records || [];
      const acronyms = new Set();
      
      tables.forEach(table => {
        if (table.TAB_AcronimoCampi && table.TAB_AcronimoCampi.trim()) {
          acronyms.add(table.TAB_AcronimoCampi.trim().toUpperCase());
        }
      });
      
      setExistingAcronyms(acronyms);
      
      console.log(`[TableAcronymModal] Caricati ${acronyms.size} acronimi esistenti:`, Array.from(acronyms));
      
    } catch (error) {
      console.error('[TableAcronymModal] Errore nel caricamento acronimi esistenti:', error);
      setValidationError('Errore nel caricamento degli acronimi esistenti. Procedi con cautela.');
    } finally {
      setIsLoadingExisting(false);
    }
  }, []);

  // Genera opzioni valide con fallback solo alfabetici (NO NUMERI)
  useEffect(() => {
    if (isOpen && tableName && existingAcronyms.size > 0) {
      let generated = generateAcronyms(tableName);
      let validOptions = generated.filter(acr => !existingAcronyms.has(acr.toUpperCase()));
      
      console.log(`[TableAcronymModal] Generati ${generated.length} acronimi, ${validOptions.length} validi iniziali:`, validOptions);
      
      // Se abbiamo meno di 4 opzioni valide, genera solo varianti alfabetiche sensate
      if (validOptions.length < 4) {
        const baseAcronyms = generated.slice(0, 2); // Prendi i 2 migliori generati
        const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        
        for (const base of baseAcronyms) {
          if (validOptions.length >= 6) break;
          
          // Solo varianti alfabetiche sensate (evita combinazioni strane)
          for (const letter of letters) {
            if (validOptions.length >= 6) break;
            
            // Variante con sostituzione della terza lettera (più naturale)
            const variant1 = base.substring(0, 2) + letter;
            if (!existingAcronyms.has(variant1) && !validOptions.includes(variant1) && variant1 !== base) {
              // Evita combinazioni improponibili (es. QQQ, XXX, ZZZ)
              if (!/[QXZ]{2,}/.test(variant1) && new Set(variant1).size >= 2) {
                validOptions.push(variant1);
              }
            }
            
            // Variante con sostituzione della seconda lettera
            const variant2 = base[0] + letter + base[2];
            if (validOptions.length < 6 && !existingAcronyms.has(variant2) && !validOptions.includes(variant2) && variant2 !== base) {
              if (!/[QXZ]{2,}/.test(variant2) && new Set(variant2).size >= 2) {
                validOptions.push(variant2);
              }
            }
          }
        }
      }
      
      setSuggestedOptions(validOptions);
      
      // Se abbiamo opzioni valide, imposta la prima come default
      if (validOptions.length > 0) {
        setAcronym(validOptions[0]);
      }
      
      console.log(`[TableAcronymModal] Finali: ${validOptions.length} opzioni valide (${existingAcronyms.size} acronimi esistenti):`, validOptions);
    }
  }, [isOpen, tableName, existingAcronyms, generateAcronyms]);

  // Carica acronimi esistenti quando si apre la modale
  useEffect(() => {
    if (isOpen) {
      loadExistingAcronyms();
    }
  }, [isOpen, loadExistingAcronyms]);

  // Validazione acronimo in tempo reale
  const validateAcronym = useCallback((value) => {
    const upperValue = value.toUpperCase().trim();
    
    if (!upperValue) {
      setValidationError('L\'acronimo è obbligatorio');
      return false;
    }
    
    if (upperValue.length !== 3) {
      setValidationError('L\'acronimo deve essere esattamente di 3 caratteri');
      return false;
    }
    
    if (!/^[A-Z]{3}$/.test(upperValue)) {
      setValidationError('L\'acronimo deve contenere solo lettere');
      return false;
    }
    
    if (existingAcronyms.has(upperValue)) {
      setValidationError('Questo acronimo è già utilizzato da un\'altra tabella');
      return false;
    }
    
    setValidationError('');
    return true;
  }, [existingAcronyms]);

  // Gestisce il cambio dell'acronimo
  const handleAcronymChange = useCallback((value) => {
    const upperValue = value.toUpperCase().slice(0, 3); // Massimo 3 caratteri, maiuscolo
    setAcronym(upperValue);
    validateAcronym(upperValue);
  }, [validateAcronym]);

  // Gestisce la selezione di un'opzione suggerita
  const handleSelectSuggestion = useCallback((suggestion) => {
    setAcronym(suggestion);
    validateAcronym(suggestion);
  }, [validateAcronym]);

  // Gestisce la conferma
  const handleConfirm = useCallback(() => {
    if (validateAcronym(acronym)) {
      onConfirm(acronym.toUpperCase().trim());
    }
  }, [acronym, validateAcronym, onConfirm]);

  // Gestisce il backdrop click
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  const isValid = acronym.length === 3 && !validationError;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className={`px-6 py-4 bg-gradient-to-r from-${themeColor}-500 to-${themeColor}-600 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <FaTable className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold">{tableName}</h2>
                <p className="text-sm opacity-90">Prima di salvare la tabella, fornire un acronimo per i suoi campi</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors duration-200"
            >
              <FaTimes className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Campo input acronimo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              Inserire acronimo a mano <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={acronym}
              onChange={(e) => handleAcronymChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && isValid && handleConfirm()}
              placeholder="Es: CBA, DCL, CFG"
              className={`w-full px-3 py-2 text-sm border rounded-lg shadow-sm transition-all duration-200
                         ${validationError 
                           ? 'border-red-500 dark:border-red-400 bg-red-50 dark:bg-red-900/20' 
                           : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                         }
                         text-gray-900 dark:text-white
                         placeholder-gray-400 dark:placeholder-gray-500
                         focus:outline-none focus:ring-2 focus:ring-${themeColor}-500 focus:border-${themeColor}-500
                         uppercase`}
              maxLength={3}
            />
            
            {/* Messaggio di validazione */}
            {validationError && (
              <div className="mt-2 flex items-center space-x-2 text-red-600 dark:text-red-400">
                <FaExclamationTriangle className="h-3 w-3" />
                <span className="text-xs">{validationError}</span>
              </div>
            )}
            
            {/* Indicatore di validità */}
            {acronym && !validationError && (
              <div className="mt-2 flex items-center space-x-2 text-green-600 dark:text-green-400">
                <FaCheckCircle className="h-3 w-3" />
                <span className="text-xs">Acronimo valido</span>
              </div>
            )}
          </div>

          {/* Opzioni suggerite */}
          {(isLoadingExisting || suggestedOptions.length > 0) && (
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <FaLightbulb className={`h-4 w-4 text-${themeColor}-600 dark:text-${themeColor}-400`} />
                <span className="text-sm font-medium text-gray-700 dark:text-white">
                  Suggerimenti
                </span>
                {isLoadingExisting && <FaSpinner className="h-3 w-3 animate-spin text-gray-400" />}
              </div>
              
              {isLoadingExisting ? (
                <div className="text-center py-4">
                  <FaSpinner className="h-6 w-6 animate-spin mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Verifica acronimi esistenti...
                  </p>
                </div>
              ) : suggestedOptions.length > 0 ? (
                <div>
                  {/* Messaggio se poche opzioni disponibili */}
                  {suggestedOptions.length < 4 && (
                    <div className="mb-3 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <FaExclamationTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                        <span className="text-xs text-amber-700 dark:text-amber-300">
                          Poche opzioni disponibili a causa di acronimi già esistenti. 
                          Si consiglia l'inserimento manuale di un acronimo personalizzato.
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-3 gap-2">
                    {suggestedOptions.slice(0, 6).map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSelectSuggestion(suggestion)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg border-2 transition-all duration-200
                                   ${acronym === suggestion
                                     ? `bg-${themeColor}-600 border-${themeColor}-600 text-white`
                                     : `bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white hover:border-${themeColor}-400`
                                   }`}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-3 text-sm text-gray-500 dark:text-gray-400">
                  <div className="mb-2">
                    <FaExclamationTriangle className="h-4 w-4 mx-auto text-amber-500" />
                  </div>
                  <p className="font-medium">Nessun suggerimento disponibile</p>
                  <p className="text-xs mt-1">Troppi acronimi già esistenti. Inserisci manualmente un acronimo personalizzato.</p>
                </div>
              )}
            </div>
          )}


        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors duration-200"
            >
              Annulla
            </button>
            <button
              onClick={handleConfirm}
              disabled={!isValid || isLoadingExisting}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors duration-200 flex items-center space-x-2
                         ${isValid && !isLoadingExisting
                           ? `bg-${themeColor}-600 hover:bg-${themeColor}-700`
                           : 'bg-gray-400 cursor-not-allowed'
                         }`}
            >
              <FaCheckCircle className="h-4 w-4" />
              <span>Conferma</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableAcronymModal;